import React, { useState } from "react";
import { message } from "antd";
import {
  InnerContainer,
  MainContainer,
  MainHeading,
  StyledButton,
  StyledRow,
  StyledTitle,
  StyledInput,
  StyledPhoneRow,
  StyledButtonSpacer,
} from "./styles";
import cache from "utils/cache";
import { useUpdateProfileMutation } from "api/authApi";

const ProfileForm = () => {
  const [editingFirstName, setEditingFirstName] = useState(false);
  const [editingLastName, setEditingLastName] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [savingField, setSavingField] = useState(null);

  const user = cache.getItem("user");
  const [firstName, setFirstName] = useState(user?.firstName);
  const [lastName, setLastName] = useState(user?.lastName);
  const [phone, setPhone] = useState(user?.phoneNumber);

  const [inputValueFirstName, setInputValueFirstName] = useState("");
  const [inputValueLastName, setInputValueLastName] = useState("");
  const [inputValuePhone, setInputValuePhone] = useState("");

  const [updateProfile] = useUpdateProfileMutation();

  const handleUpdate = (fieldName) => {
    switch (fieldName) {
      case "firstName":
        setInputValueFirstName(firstName);
        setEditingFirstName(true);
        break;
      case "lastName":
        setInputValueLastName(lastName);
        setEditingLastName(true);
        break;
      case "phone":
        setInputValuePhone(phone);
        setEditingPhone(true);
        break;
      default:
        break;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case "firstName":
        setInputValueFirstName(value);
        break;
      case "lastName":
        setInputValueLastName(value);
        break;
      case "phone":
        setInputValuePhone(value);
        break;
      default:
        break;
    }
  };

  const closeEditing = (fieldName) => {
    switch (fieldName) {
      case "firstName":
        setEditingFirstName(false);
        break;
      case "lastName":
        setEditingLastName(false);
        break;
      case "phone":
        setEditingPhone(false);
        break;
      default:
        break;
    }
  };

  const buildPayload = (fieldName) => {
    switch (fieldName) {
      case "firstName":
        return { firstName: inputValueFirstName };
      case "lastName":
        return { lastName: inputValueLastName };
      case "phone":
        return { phoneNumber: inputValuePhone };
      default:
        return {};
    }
  };

  const applyLocalValue = (fieldName) => {
    switch (fieldName) {
      case "firstName":
        setFirstName(inputValueFirstName);
        break;
      case "lastName":
        setLastName(inputValueLastName);
        break;
      case "phone":
        setPhone(inputValuePhone);
        break;
      default:
        break;
    }
  };

  const handleSave = async (fieldName) => {
    const payload = buildPayload(fieldName);
    setSavingField(fieldName);

    try {
      const res = await updateProfile(payload);

      if (res.error) {
        const errorMessage =
          res.error?.data?.message || "Failed to update profile. Please try again.";
        message.error(errorMessage);
        return;
      }

      const updatedUser = res.data?.user;
      if (updatedUser) {
        const cachedUser = cache.getItem("user") || {};
        cache.setItem("user", {
          ...cachedUser,
          firstName: updatedUser.firstName,
          lastName: updatedUser.lastName,
          email: updatedUser.email,
          phoneNumber: updatedUser.phoneNumber,
        });
      }

      applyLocalValue(fieldName);
      closeEditing(fieldName);
      message.success("Profile updated successfully");
    } catch {
      message.error("Failed to update profile. Please try again.");
    } finally {
      setSavingField(null);
    }
  };

  const isSaving = (fieldName) => savingField === fieldName;

  return (
    <MainContainer>
      <InnerContainer>
        <MainHeading>{`Profile`}</MainHeading>

        <StyledRow>
          <StyledTitle>First Name</StyledTitle>
          {editingFirstName ? (
            <StyledInput
              type="text"
              name="firstName"
              value={inputValueFirstName}
              onChange={handleInputChange}
            />
          ) : (
            <StyledTitle>{firstName}</StyledTitle>
          )}
          <StyledButton
            disabled={isSaving("firstName")}
            onClick={() =>
              editingFirstName
                ? handleSave("firstName")
                : handleUpdate("firstName")
            }
          >
            {editingFirstName ? (isSaving("firstName") ? "Saving..." : "Save") : "Update"}
          </StyledButton>
        </StyledRow>

        <StyledRow>
          <StyledTitle>Last Name</StyledTitle>
          {editingLastName ? (
            <StyledInput
              type="text"
              name="lastName"
              value={inputValueLastName}
              onChange={handleInputChange}
            />
          ) : (
            <StyledTitle>{lastName}</StyledTitle>
          )}
          <StyledButton
            disabled={isSaving("lastName")}
            onClick={() =>
              editingLastName
                ? handleSave("lastName")
                : handleUpdate("lastName")
            }
          >
            {editingLastName ? (isSaving("lastName") ? "Saving..." : "Save") : "Update"}
          </StyledButton>
        </StyledRow>

        <StyledRow>
          <StyledTitle>Email Address</StyledTitle>
          <StyledTitle>{user?.email}</StyledTitle>
          <StyledButtonSpacer aria-hidden="true">Update</StyledButtonSpacer>
        </StyledRow>

        <StyledPhoneRow>
          <StyledTitle>Phone Number</StyledTitle>
          {editingPhone ? (
            <StyledInput
              type="text"
              name="phone"
              value={inputValuePhone}
              onChange={handleInputChange}
            />
          ) : (
            <StyledTitle>{phone}</StyledTitle>
          )}
          <StyledButton
            disabled={isSaving("phone")}
            onClick={() =>
              editingPhone ? handleSave("phone") : handleUpdate("phone")
            }
          >
            {editingPhone ? (isSaving("phone") ? "Saving..." : "Save") : "Update"}
          </StyledButton>
        </StyledPhoneRow>
      </InnerContainer>
    </MainContainer>
  );
};

export default ProfileForm;
